import React, { createContext, useContext } from "react";

/**
 * React Context for authentication state and actions.
 *
 * - Defines the AuthApi interface used by all auth implementations (mock, MSAL, etc.).
 * - Provides a default unauthenticated context with no-op login/logout.
 * - `useAuth()`: hook to access the current AuthApi anywhere in the component tree.
 * - `AuthProvider`: wraps the app with a chosen AuthApi implementation, making it
 *   available via React Context to all child components.
 */

export type AuthApi = {
    isAuthenticated: boolean;
    account?: { oid: string; name: string; email?: string; givenName?: string };
    login: () => Promise<void>;
    logout: () => Promise<void>;
    getAccessToken?: () => Promise<string | null>;
};

const Ctx = createContext<AuthApi>({
    isAuthenticated: false,
    login: async () => {},
    logout: async () => {},
});

export const useAuth = () => useContext(Ctx);
export const AuthProvider = ({ impl, children }: { impl: AuthApi; children: React.ReactNode }) =>
    <Ctx.Provider value={impl}>{children}</Ctx.Provider>;
